import { useCallback, useRef, useState } from 'react'

import { useFormInstanceFn } from '../../../shared/antd/ANTDForm'
import { getBase64 } from '../../../utils'
import { getImageFileApi } from '../common.api'

const signatureCanvas = ({
  value,
  onChange,
  disabled,
  filePath,
  apiCall,
  nestedKey,
}) => {
  const signatureRef = useRef(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tempSignature, setTempSignature] = useState(null)
  const form = useFormInstanceFn()
  const [imagePreview, setImagePreview] = useState({
    visible: false,
    image: '',
    type: '',
  })

  const getDataURL = useCallback(async (callApi = true) => {
    if (signatureRef.current?.isEmpty()) return null

    if (callApi) {
      try {
        const canvas = signatureRef.current.getCanvas()
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(blob => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to convert canvas to blob'))
            }
          }, 'image/png')
        })
        const file = new File([blob], 'signature.png', {
          type: 'image/png',
        })
        const formData = new FormData()
        formData.append('file', file)
        const resp = await getImageFileApi({
          params: '?source=USER',
          payload: formData,
        })
        return { dmsId: resp?.data?.dmsId || null, file }
      } catch (error) {
        return null
      }
    } else {
      return signatureRef.current?.toDataURL('image/png') || null
    }
  }, [])

  const handleOpenModal = useCallback(() => {
    if (signatureRef.current) {
      signatureRef.current?.clear()
    }
    if (!disabled) setIsModalOpen(true)
  }, [disabled])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setTempSignature(null)
  }, [])

  const handleClear = useCallback(() => {
    signatureRef.current?.clear()
    setTempSignature(null)
  }, [])

  const handleSave = async () => {
    const fileData = await getDataURL(true)
    if (fileData?.dmsId && fileData?.file) {
      const url = await getBase64(fileData.file)
      form.setFieldValue(filePath, {
        fileList: [
          {
            ...fileData?.file,
            dmsId: fileData?.dmsId,
            uid: fileData?.dmsId,
            originFileObj: fileData?.file,
            url: url,
            type: 'image/png',
          },
        ],
      })
      if (apiCall) {
        await apiCall({
          key: nestedKey,
          index: filePath?.[1],
          isLoading: false,
          showMsg: false,
          closeFieldModal: false,
        })
      }
      setIsModalOpen(false)
    }
  }

  const handleEnd = useCallback(async () => {
    const dataURL = await getDataURL(false)
    setTempSignature(dataURL)
  }, [getDataURL])

  const handlePreview = () => {
    setImagePreview({
      visible: true,
      image: value?.fileList?.[0]?.url,
      type: value?.fileList?.[0]?.type || 'image/png',
    })
  }

  const handleCancelPreview = () => {
    setImagePreview({ visible: false, image: null, type: null })
  }

  const handleRemove = () => {
    if (filePath) {
      form.setFieldValue(filePath, {
        fileList: [],
      })
      if (apiCall) {
        apiCall({
          key: nestedKey,
          index: filePath?.[1],
          isLoading: false,
          showMsg: false,
          closeFieldModal: false,
        })
      }
    }
  }

  return {
    signatureRef,
    isModalOpen,
    tempSignature,
    handleOpenModal,
    handleCloseModal,
    handleClear,
    handleSave,
    handleEnd,
    imagePreview,
    handlePreview,
    handleCancelPreview,
    handleRemove,
  }
}

export default signatureCanvas
